import { parseScopedThreadKey, scopeThreadRef, scopedThreadKey } from "@cmmd-forge/client-runtime";
import { type EnvironmentId, type ScopedThreadRef, type ThreadId } from "@cmmd-forge/contracts";
import { useEffect, useMemo, useState } from "react";
import {
  deriveMountedTerminalThreadIds,
  MAX_HIDDEN_MOUNTED_TERMINAL_THREADS,
} from "../ChatView.logic";

export function useMountedTerminalThreadRefs(input: {
  readonly terminalStateByThreadKey: Record<string, { readonly terminalOpen: boolean }>;
  readonly environmentStateById: Record<
    string,
    {
      readonly threadShellById: Record<
        string,
        { readonly environmentId: EnvironmentId; readonly id: ThreadId }
      >;
    }
  >;
  readonly draftThreadsByThreadKey: Record<
    string,
    { readonly environmentId: EnvironmentId; readonly threadId: ThreadId }
  >;
  readonly activeThreadKey: string | null;
  readonly activeThreadTerminalOpen: boolean;
}): {
  readonly mountedTerminalThreadRefs: ReadonlyArray<{
    readonly key: string;
    readonly threadRef: ScopedThreadRef;
  }>;
} {
  const openTerminalThreadKeys = useMemo(
    () =>
      Object.entries(input.terminalStateByThreadKey).flatMap(
        ([nextThreadKey, nextTerminalState]) =>
          nextTerminalState.terminalOpen ? [nextThreadKey] : [],
      ),
    [input.terminalStateByThreadKey],
  );
  const serverThreadKeys = useMemo(
    () =>
      Object.values(input.environmentStateById).flatMap((environmentState) =>
        Object.values(environmentState.threadShellById).map((thread) =>
          scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id)),
        ),
      ),
    [input.environmentStateById],
  );
  const draftThreadKeys = useMemo(
    () =>
      Object.values(input.draftThreadsByThreadKey).map((draftThread) =>
        scopedThreadKey(scopeThreadRef(draftThread.environmentId, draftThread.threadId)),
      ),
    [input.draftThreadsByThreadKey],
  );
  const [mountedTerminalThreadKeys, setMountedTerminalThreadKeys] = useState<string[]>([]);
  const mountedTerminalThreadRefs = useMemo(
    () =>
      mountedTerminalThreadKeys.flatMap((mountedThreadKey) => {
        const mountedThreadRef = parseScopedThreadKey(mountedThreadKey);
        return mountedThreadRef ? [{ key: mountedThreadKey, threadRef: mountedThreadRef }] : [];
      }),
    [mountedTerminalThreadKeys],
  );

  useEffect(() => {
    setMountedTerminalThreadKeys((currentThreadIds) => {
      const nextThreadIds = deriveMountedTerminalThreadIds({
        currentThreadIds,
        openTerminalThreadKeys,
        serverThreadKeys,
        draftThreadKeys,
        activeThreadKey: input.activeThreadKey,
        activeThreadTerminalOpen: input.activeThreadTerminalOpen,
        maxHiddenThreadCount: MAX_HIDDEN_MOUNTED_TERMINAL_THREADS,
      });
      return currentThreadIds.length === nextThreadIds.length &&
        currentThreadIds.every((nextThreadId, index) => nextThreadId === nextThreadIds[index])
        ? currentThreadIds
        : nextThreadIds;
    });
  }, [
    input.activeThreadKey,
    input.activeThreadTerminalOpen,
    draftThreadKeys,
    openTerminalThreadKeys,
    serverThreadKeys,
  ]);

  return { mountedTerminalThreadRefs };
}
